/**
 * Shows the matching shade-guide item under the selected shade picker.
 * - Buy 1 (swatch picker): updates the global display
 * - Buy 3 (dropdown picker): each dropdown updates only its own display
 */
if (customElements.get('aiw-shade-selected-display') === undefined) {
  class AiwShadeSelectedDisplay extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.labelEnterListeners = [];
      this.labelLeaveListeners = [];
      // Track every display element that has a pending fade timer so we can
      // cancel them on disconnect (theme-editor block reorder, etc.) before
      // the timer fires against a detached node.
      this.fadeDisplays = new Set();
    }

    connectedCallback() {
      this.root = this.closest('.aiw-v2-main-product') || document;
      this.source = this.root.querySelector('.shade-guide-source');
      if (!this.source) return;

      this.root.addEventListener('change', this.onChange);

      this.root.querySelectorAll('.concealer-variant__shade-label, .swatch-input__label').forEach((label) => {
        const radio = this.getRadioFromLabel(label);
        if (!radio || radio.type !== 'radio') return;

        const isBuy3 = radio.classList.contains('concealer-variant__shade-radio');

        const enter = () => {
          const display = isBuy3 ? this.findDisplay(radio) : this.root.querySelector('[data-shade-selected="global"]');
          this.renderShade(display, radio.dataset.shadeTitle || radio.value);
        };

        const leave = () => {
          if (isBuy3) {
            const container = radio.closest('.select.concealer-variant__shade');
            const display = container && container.querySelector('[data-shade-selected="local"]');
            this.renderShade(display, this.getCheckedTitle(container, '.concealer-variant__shade-radio'));
          } else {
            const globalDisplay = this.root.querySelector('[data-shade-selected="global"]');
            this.renderShade(globalDisplay, this.getCheckedTitle(this.root, '.swatch-input__input'));
          }
        };

        label.addEventListener('mouseenter', enter);
        label.addEventListener('mouseleave', leave);
        this.labelEnterListeners.push({ label, fn: enter });
        this.labelLeaveListeners.push({ label, fn: leave });
      });

      this.updateAll();
    }

    disconnectedCallback() {
      if (this.root) this.root.removeEventListener('change', this.onChange);
      this.labelEnterListeners.forEach(({ label, fn }) => label.removeEventListener('mouseenter', fn));
      this.labelLeaveListeners.forEach(({ label, fn }) => label.removeEventListener('mouseleave', fn));
      this.fadeDisplays.forEach((display) => {
        if (display._fadeTimer) {
          clearTimeout(display._fadeTimer);
          display._fadeTimer = null;
        }
      });
      this.fadeDisplays.clear();
    }

    findDisplay(radio) {
      const localContainer = radio.closest('.select.concealer-variant__shade');
      if (localContainer) {
        return localContainer.querySelector('[data-shade-selected="local"]');
      }
      return this.root.querySelector('[data-shade-selected="global"]');
    }

    renderShade(display, title) {
      if (!display) return;

      let html = '';
      if (title) {
        const match = this.source.querySelector('[data-shade-name="' + title.toLowerCase().trim() + '"]');
        const text = match && match.querySelector('.shade-guide__text');
        if (text) html = text.outerHTML;
      }

      if (display.innerHTML === html) return;

      if (!display.style.transition) {
        display.style.transition = 'opacity 0.05s ease';
      }

      if (display._fadeTimer) clearTimeout(display._fadeTimer);
      display.style.opacity = '0';

      this.fadeDisplays.add(display);
      display._fadeTimer = setTimeout(() => {
        display.innerHTML = html;
        display.style.opacity = '1';
        display._fadeTimer = null;
        this.fadeDisplays.delete(display);
      }, 45);
    }

    updateAll() {
      this.root.querySelectorAll('.select.concealer-variant__shade').forEach((container) => {
        const checked = container.querySelector('.concealer-variant__shade-radio:checked');
        const display = container.querySelector('[data-shade-selected="local"]');
        this.renderShade(display, checked ? checked.dataset.shadeTitle || checked.value : '');
      });

      const buy1 = this.root.querySelector('.swatch-input__input:checked');
      const globalDisplay = this.root.querySelector('[data-shade-selected="global"]');
      this.renderShade(globalDisplay, buy1 ? buy1.dataset.shadeTitle || buy1.value : '');
    }

    onChange(e) {
      const t = e.target;
      if (!t || t.type !== 'radio') return;
      if (t.classList.contains('concealer-variant__shade-radio')) {
        const display = this.findDisplay(t);
        this.renderShade(display, t.dataset.shadeTitle || t.value);
      } else if (t.classList.contains('swatch-input__input')) {
        const globalDisplay = this.root.querySelector('[data-shade-selected="global"]');
        this.renderShade(globalDisplay, t.dataset.shadeTitle || t.value);
      }
    }

    getRadioFromLabel(label) {
      return label.querySelector('input[type="radio"]') || label.previousElementSibling;
    }

    getCheckedTitle(scope, selector) {
      const checked = scope.querySelector(selector + ':checked');
      return checked ? checked.dataset.shadeTitle || checked.value : '';
    }
  }

  customElements.define('aiw-shade-selected-display', AiwShadeSelectedDisplay);
}
